import chalk from 'chalk';
import ora from 'ora';
import { select, input } from '@inquirer/prompts';
import { writeFileSync, mkdirSync } from 'node:fs';
import { join } from 'node:path';
import {
  DEPLOYMENT_TEMPLATES,
  getTemplate,
  renderTemplate,
  type TemplateContext,
  type TemplateAgent,
} from '@augmented/core';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, info } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

interface DeployOptions {
  template?: string;
  port?: string;
}

/**
 * `agt deploy`
 */
export async function deployCommand(opts: DeployOptions): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const nonInteractive = !!opts.template;
  const json = isJsonMode();

  let templateId: string;
  let basePort: number;

  if (nonInteractive) {
    templateId = opts.template!;
    basePort = Number(opts.port ?? '9000');
    if (isNaN(basePort) || basePort < 1 || basePort > 65535) {
      if (json) {
        jsonOutput({ ok: false, error: 'Invalid port number' });
      } else {
        error('Invalid port number. Must be between 1 and 65535.');
      }
      process.exitCode = 1;
      return;
    }
  } else {
    console.log(chalk.bold('\nAugmented — Deploy\n'));

    templateId = await select({
      message: 'Deployment template:',
      choices: DEPLOYMENT_TEMPLATES.map((t) => ({
        value: t.id,
        name: `${t.name} — ${t.description}`,
      })),
    });

    const basePortStr = await input({
      message: 'Base port for gateway:',
      default: '9000',
      validate: (v) => {
        const n = Number(v);
        return (!isNaN(n) && n > 0 && n < 65536) || 'Must be a valid port number';
      },
    });
    basePort = Number(basePortStr);
  }

  const tmpl = getTemplate(templateId);
  if (!tmpl) {
    if (json) {
      jsonOutput({ ok: false, error: `Template "${templateId}" not found` });
    } else {
      error(`Template "${templateId}" not found.`);
    }
    process.exitCode = 1;
    return;
  }

  // ── Fetch agents via API ──────────────────────────────────────────────

  const spinner = ora({ text: 'Fetching agents…', isSilent: json });
  spinner.start();

  let agents: Array<{
    agent_id: string;
    code_name: string;
    display_name: string;
    environment: string;
  }>;

  try {
    const data = await api.get<{ agents: typeof agents }>('/agents');
    agents = data.agents;
  } catch (err) {
    spinner.fail('Failed to fetch agents.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
    return;
  }

  if (!agents || agents.length === 0) {
    spinner.fail('No agents found in team.');
    if (json) {
      jsonOutput({ ok: false, error: 'No agents found in team. Run `agt init` first.' });
    } else {
      error('No agents found in team. Run `agt init` first.');
    }
    process.exitCode = 1;
    return;
  }

  // ── Build template context ────────────────────────────────────────────

  const templateAgents: TemplateAgent[] = agents.map((a, i) => ({
    agent_id: a.agent_id,
    code_name: a.code_name,
    display_name: a.display_name,
    environment: a.environment,
    port: basePort + i,
  }));

  const context: TemplateContext = {
    agents: templateAgents,
    gateway: { port: basePort },
    variables: {},
  };

  let rendered: string;
  try {
    rendered = renderTemplate(tmpl.template, context);
  } catch (err) {
    spinner.fail('Template rendering failed.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
    return;
  }

  // ── Record deployment via API ─────────────────────────────────────────

  try {
    await api.post('/deployments', {
      template_id: tmpl.id,
      agent_ids: agents.map((a) => a.agent_id),
      variables: { base_port: basePort },
    });
  } catch (deployErr) {
    spinner.stop();
    if (!json) info(`Warning: could not record deployment in database: ${(deployErr as Error).message}`);
  }

  // ── Write output file ─────────────────────────────────────────────────

  const outDir = join(process.cwd(), '.augmented', 'deploy');
  mkdirSync(outDir, { recursive: true });
  const outPath = join(outDir, 'docker-compose.yaml');
  writeFileSync(outPath, rendered);

  spinner.succeed('Deployment config generated.');

  if (json) {
    jsonOutput({
      ok: true,
      template: tmpl.id,
      agents: agents.map((a) => a.code_name),
      output_path: outPath,
    });
    return;
  }

  console.log();
  info(`Template: ${chalk.bold(tmpl.name)}`);
  info(`Agents:   ${agents.map((a) => a.code_name).join(', ')}`);
  info(`Output:   ${outPath}`);
  console.log();
  info(`Next steps:`);
  info(`  cd .augmented/deploy && docker compose up`);
}
